import { useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlayIcon,
  PauseIcon,
  BackwardIcon,
  ForwardIcon,
  SpeakerWaveIcon,
  ArrowDownTrayIcon,
  PaperAirplaneIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Input from '@/components/ui/Input';
import clsx from 'clsx';

// Mock data
const mockAudiobook = {
  id: 'job_abc123',
  title: 'Le Petit Prince',
  voice: 'Cath Brunet',
  duration: '2:34:00',
  totalDuration: 9240, // seconds
  chapters: [
    { id: 1, name: 'Chapter 1 - Introduction', duration: 323, startTime: 0 },
    { id: 2, name: 'Chapter 2 - The Asteroid', duration: 465, startTime: 323 },
    { id: 3, name: 'Chapter 3 - The Rose', duration: 372, startTime: 788 },
    { id: 4, name: 'Chapter 4 - The Little Prince Leaves', duration: 298, startTime: 1160 },
    { id: 5, name: 'Chapter 5 - The Baobabs', duration: 412, startTime: 1458 },
    { id: 6, name: 'Chapter 6 - The Sunset', duration: 287, startTime: 1870 },
    { id: 7, name: 'Chapter 7 - The Flower', duration: 356, startTime: 2157 },
    { id: 8, name: 'Chapter 8 - The Sheep', duration: 398, startTime: 2513 },
  ],
};

const mockQANotes = [
  { id: 1, timestamp: 323, note: 'Pronunciation issue "Saint-Exupéry"', resolved: false },
  { id: 2, timestamp: 745, note: 'Audio glitch at chapter transition', resolved: true },
  { id: 3, timestamp: 1240, note: 'Volume inconsistency', resolved: false },
];

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function TTSPlayerPage() {
  const { jobId } = useParams();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(754);
  const [volume, setVolume] = useState(80);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [activeChapter, setActiveChapter] = useState(2);
  const [newNote, setNewNote] = useState('');
  const progressRef = useRef<HTMLDivElement>(null);

  const progress = (currentTime / mockAudiobook.totalDuration) * 100;

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (progressRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickPosition = (e.clientX - rect.left) / rect.width;
      setCurrentTime(Math.floor(clickPosition * mockAudiobook.totalDuration));
    }
  };

  const jumpToChapter = (chapter: typeof mockAudiobook.chapters[0]) => {
    setCurrentTime(chapter.startTime);
    setActiveChapter(chapter.id);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link
        to={`/tts/jobs/${jobId}`}
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Job
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Audio Player</h1>
          <p className="text-slate-500 mt-1">Job: {jobId || 'demo'}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}>
            Download MP3
          </Button>
          <Button variant="outline" leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}>
            Download ZIP
          </Button>
          <Button leftIcon={<PaperAirplaneIcon className="w-4 h-4" />}>
            Send to Distribution
          </Button>
        </div>
      </div>

      {/* Player Card */}
      <Card>
        <CardBody>
          {/* Title & Info */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-slate-800">📖 {mockAudiobook.title}</h2>
            <p className="text-slate-500 mt-1">
              Voice: {mockAudiobook.voice} | Duration: {mockAudiobook.duration} | Chapters: {mockAudiobook.chapters.length}
            </p>
          </div>

          {/* Player Controls */}
          <div className="max-w-2xl mx-auto">
            {/* Progress Bar */}
            <div
              ref={progressRef}
              onClick={handleProgressClick}
              className="relative h-2 bg-slate-200 rounded-full cursor-pointer mb-2"
            >
              <div
                className="absolute left-0 top-0 h-full bg-primary-400 rounded-full"
                style={{ width: `${progress}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-primary-400 rounded-full shadow-md"
                style={{ left: `calc(${progress}% - 8px)` }}
              />
            </div>

            {/* Time Display */}
            <div className="flex justify-between text-sm text-slate-500 mb-6">
              <span>{formatTime(currentTime)}</span>
              <span>{mockAudiobook.duration}</span>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <button
                className="p-3 rounded-full text-slate-600 hover:bg-slate-100 transition-colors"
                onClick={() => setCurrentTime(Math.max(0, currentTime - 10))}
              >
                <BackwardIcon className="w-6 h-6" />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="w-16 h-16 rounded-full bg-primary-400 text-white flex items-center justify-center shadow-lg hover:bg-primary-500 transition-colors"
              >
                {isPlaying ? (
                  <PauseIcon className="w-8 h-8" />
                ) : (
                  <PlayIcon className="w-8 h-8 ml-1" />
                )}
              </button>
              <button
                className="p-3 rounded-full text-slate-600 hover:bg-slate-100 transition-colors"
                onClick={() => setCurrentTime(Math.min(mockAudiobook.totalDuration, currentTime + 10))}
              >
                <ForwardIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Volume & Speed */}
            <div className="flex items-center justify-center gap-8">
              <div className="flex items-center gap-2">
                <SpeakerWaveIcon className="w-5 h-5 text-slate-500" />
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume}
                  onChange={(e) => setVolume(parseInt(e.target.value))}
                  className="w-24 accent-primary-400"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Speed:</span>
                {[0.5, 1, 1.5, 2].map((speed) => (
                  <button
                    key={speed}
                    onClick={() => setPlaybackSpeed(speed)}
                    className={clsx(
                      'px-2 py-1 rounded text-sm font-medium transition-colors',
                      playbackSpeed === speed
                        ? 'bg-primary-100 text-primary-600'
                        : 'text-slate-600 hover:bg-slate-100'
                    )}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chapters */}
        <Card>
          <CardHeader title="Chapters" />
          <CardBody className="p-0">
            <div className="max-h-96 overflow-y-auto">
              {mockAudiobook.chapters.map((chapter) => (
                <button
                  key={chapter.id}
                  onClick={() => jumpToChapter(chapter)}
                  className={clsx(
                    'w-full flex items-center justify-between px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors text-left',
                    activeChapter === chapter.id && 'bg-primary-50'
                  )}
                >
                  <div className="flex items-center gap-3">
                    {activeChapter === chapter.id ? (
                      <PlayIcon className="w-4 h-4 text-primary-500" />
                    ) : (
                      <span className="w-4 h-4 rounded-full border-2 border-slate-300" />
                    )}
                    <span className={activeChapter === chapter.id ? 'font-medium text-primary-600' : 'text-slate-700'}>
                      {chapter.name}
                    </span>
                  </div>
                  <span className="text-sm text-slate-500">{formatTime(chapter.duration)}</span>
                </button>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* QA Notes */}
        <Card>
          <CardHeader title="QA Notes" subtitle="Add timestamp notes for review" />
          <CardBody>
            {/* Add Note */}
            <div className="flex gap-2 mb-4">
              <div className="flex-1">
                <Input
                  placeholder="Add a note at current timestamp..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />
              </div>
              <Button leftIcon={<PlusIcon className="w-4 h-4" />}>
                Add
              </Button>
            </div>

            {/* Notes List */}
            <div className="space-y-3">
              {mockQANotes.map((note) => (
                <div
                  key={note.id}
                  className={clsx(
                    'p-3 rounded-xl border',
                    note.resolved
                      ? 'bg-success-50 border-success-200'
                      : 'bg-warning-50 border-warning-200'
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <button
                        onClick={() => setCurrentTime(note.timestamp)}
                        className="text-sm font-mono text-primary-500 hover:underline"
                      >
                        {formatTime(note.timestamp)}
                      </button>
                      <p className="text-sm text-slate-700 mt-1">{note.note}</p>
                    </div>
                    <Badge variant={note.resolved ? 'success' : 'warning'} size="sm">
                      {note.resolved ? 'Resolved' : 'Open'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

